import * as React from 'react';
import { memo, useCallback, useMemo, useState, useEffect } from 'react';
import {
  Dialog,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Typography
} from '@mui/material';
import ArrowBackSharpIcon from '@mui/icons-material/ArrowBackSharp';
import { makeStyles } from '@mui/styles';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { ConnectorInfo, DagConfigs } from 'types/types';
import { useScrollbarStyles } from 'modules/common/styles';
import { useRepliconNavigationContext } from 'modules/common/hooks/RepliconNavigationProvider';
import { RepliconMenuFooter } from 'modules/common/RepliconMenu';
import { useSidePanelContext } from 'modules/rootPage';
import { keyValueApi } from 'types/types';
import { getRequest } from 'utils/helper';
import { ConnectorDetailSidePanel } from './ConnectorDetailSidePanel';
import ConnectorOverviewPage from './ConnectorOverviewPage';
import { UninstallDialog } from './UninstallDialog';
import { ToastSnackBar } from './Setting/ToastSnackBar';

const useStyles = makeStyles((theme) => ({
  appBar: {
    position: 'relative',
    background: theme.palette.common.white,
    color: theme.palette.common.black
  },
  appBarText: {
    fontSize: theme.spacing(2.9),
    marginLeft: theme.spacing(1)
  },
  detailDialog: {
    paddingLeft: ({ paddingLeft }: { paddingLeft: number }) => paddingLeft,
    zIndex: 100
  },
  detailDialogPaper: {
    overflow: 'hidden'
  },
  toolbar: {
    minHeight: theme.spacing(6)
  },
  container: {
    backgroundColor: theme.palette.information.pageBackground,
    height: 'calc(100vh - 40px)',
    display: 'flex'
  },
  verticalDivider: { borderBottomWidth: '1px' },
  div: {
    width: '98%',
    overflow: 'hidden auto',
    height: '88vh'
  }
}));

type ConnectorDetailsProps = {
  open: boolean;
  handleClose: () => void;
  info: ConnectorInfo;
};

// Custom hook for fetching dynamic info, dag configuration
const useDagConfigHook = (info: ConnectorDetailsProps['info']) => {
  const { shortName, companyKey, installed } = info;

  const [dynamicInfo, setDynamicInfo] = useState<ConnectorInfo>();
  const [dagConfig, setDagConfig] = useState<DagConfigs>([]);
  const [isLoading, setIsLoading] = useState<boolean>(Boolean(installed));

  useEffect(() => {
    const fetchDataFromMiddleware = async () => {
      if (!installed) return;
      const queryParams = {
        connectorName: shortName,
        companyKey: companyKey,
        key: keyValueApi.dagSettings
      };
      try {
        const res = await getRequest(
          '/integration-settings-api/dag-configuration',
          queryParams,
          true
        );
        if (res.data) {
          setDagConfig(res.data);
          setIsLoading(false);
        }
      } catch (error: any) {
        console.error(
          'Error fetching dag settings info:',
          error.response || error
        );
      }

      if (shortName === 'replicon') {
        const queryParams = {
          connectorName: shortName,
          companyKey: companyKey,
          key: keyValueApi.dynamicDagConfigInfo
        };

        try {
          const res = await getRequest(
            '/integration-settings-api/dag-configuration',
            queryParams,
            true
          );

          if (res.data) {
            const {
              data: { descriptionTextArray, dagsSection }
            } = res;
            setDynamicInfo({
              ...info,
              descriptionTextArray: descriptionTextArray,
              dagsSection: dagsSection
            });
          }
        } catch (error: any) {
          console.error(
            'Error fetching dynamic info:',
            error.response || error
          );
        }
      }
    };

    fetchDataFromMiddleware();
  }, [companyKey, info, installed, setDagConfig, setIsLoading, shortName]);

  return {
    dagConfig: dagConfig,
    isLoading: isLoading,
    dynamicInfo: dynamicInfo
  };
};

export const ConnectorDetails: React.FC<ConnectorDetailsProps> = memo(
  ({ open, handleClose, info }) => {
    const { name, shortName } = info;

    const [dialogOpen, setDialogOpen] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);
    const [isSnackBarOpen, setIsSnackBarOpen] = useState<boolean>(false);
    const { formatMessage } = useIntl();
    const alertMessage = useMemo(() => {
      return formatMessage(
        {
          id: 'connectors.uninstallWarning'
        },
        {
          connectorName: name
        }
      );
    }, [formatMessage, name]);
    const [dagSetting, setDagSetting] = useState<{
      dagId: string;
      status: string;
    } | null>(null); // State is initialised here for child components
    const navigationMetadata = useRepliconNavigationContext();
    const { dagConfig, isLoading, dynamicInfo } = useDagConfigHook(info);
    const onClose = useCallback(() => handleClose(), [handleClose]);

    const getDagConfig = useCallback(() => dagConfig, [dagConfig]);
    const isAllDagDisabled = useMemo(() => {
      if (!dagConfig || !Array.isArray(dagConfig) || dagConfig.length === 0) {
        return false;
      }
      if (dagSetting) {
        dagConfig.find((dag) => {
          if (dag.workflowId === dagSetting.dagId) {
            dag.enabled = dagSetting.status;
          }
        });
      }
      return dagConfig.every((dag) => dag['enabled'] === 'No');
    }, [dagConfig, dagSetting]);

    const handleDialogOpen = useCallback(() => {
      if (!isAllDagDisabled) {
        setIsSnackBarOpen(true);
        return;
      }
      setDialogOpen(true);
    }, [isAllDagDisabled, setDialogOpen]);
    const handleDialogClose = useCallback(() => setDialogOpen(false), []);

    const onSnackbarClose = useCallback(() => {
      setIsSnackBarOpen(false);
    }, []);

    const { pinned, isDocked } = useSidePanelContext();
    const classes = useStyles({
      paddingLeft: isDocked ? 0 : pinned ? 240 : 64
    });
    const classesScrollbar = useScrollbarStyles();

    return (
      <div>
        <Dialog
          fullScreen
          open={open}
          PaperProps={{ elevation: 0, className: classes.detailDialogPaper }}
          data-testid="connectorDetailsDialog"
          className={classes.detailDialog}
          hideBackdrop={true}
        >
          <AppBar
            className={classes.appBar}
            elevation={0}
            data-testid="connectorAppBar"
          >
            <Toolbar className={classes.toolbar}>
              <IconButton
                edge="start"
                color="inherit"
                onClick={onClose}
                data-testid="connectorBackIcon"
              >
                <ArrowBackSharpIcon />
              </IconButton>
              <Typography
                className={classes.appBarText}
                variant={'subtitle2'}
                data-testid="connectorDetailName"
              >
                {name}
              </Typography>
            </Toolbar>
          </AppBar>
          <Divider data-testid="connectorAppBarDivider" />
          <div className={classes.container}>
            <ConnectorDetailSidePanel
              info={info}
              handleDialogOpen={handleDialogOpen}
              isInstalled={Boolean(info.installed)}
            />
            <Divider
              orientation="vertical"
              className={classes.verticalDivider}
              data-testid="connectorVerticalDivider"
            />
            <div
              className={classnames(classes.div, classesScrollbar.scrollbar)}
            >
              <ConnectorOverviewPage
                info={
                  shortName === 'replicon' && dynamicInfo ? dynamicInfo : info
                }
                installId={'install-id'}
                dagConfig={getDagConfig()}
                isLoading={isLoading}
                dagSetting={dagSetting}
                setDagSetting={setDagSetting}
              />
            </div>
            <UninstallDialog
              connectorName={info.shortName}
              companyKey={info.companyKey}
              dialogOpen={dialogOpen}
              handleDialogClose={handleDialogClose}
              name={name}
              isDisconnecting={isDisconnecting}
              setIsDisconnecting={setIsDisconnecting}
            />
          </div>
          {navigationMetadata?.footerMetadata && (
            <RepliconMenuFooter NavigationMetaData={navigationMetadata} />
          )}
          <ToastSnackBar
            show={isSnackBarOpen}
            category={'error'}
            message={alertMessage}
            onClose={onSnackbarClose}
          />
        </Dialog>
      </div>
    );
  }
);

ConnectorDetails.displayName = 'ConnectorDetails';
